import React, { useState, useEffect } from 'react';
import axios from "../../../../axios-order";
import { Snackbar, Alert } from '@mui/material';

function Sixteen({ handleBack, updateTableData, data, reasonsData, title, get_gstr9_report_data }) {
  const [inputValues, setInputValues] = useState([]);
  const [reasonText, setReasonText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Initialize manual inputs and reason text
  useEffect(() => {
    if (data) {
      setInputValues(data.map(item =>
        item.manual_input != null ? String(item.manual_input) : ''
      ));
    }
    setReasonText(reasonsData?.[0]?.reasons || '');
  }, [data, reasonsData]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // Improved input change handler with proper negative & decimal support
  const handleManualInputChange = (index, value) => {
    let clean = value.replace('₹', '').trim();

    if (clean === '') {
      const newValues = [...inputValues];
      newValues[index] = '';
      setInputValues(newValues);

      const newData = [...data];
      newData[index] = { ...newData[index], manual_input: null };
      updateTableData?.(newData);
      return;
    }

    // Ensure only one minus at the beginning
    if (clean.includes('-')) {
      clean = '-' + clean.replace(/-/g, '');
    }

    // Validate: optional minus, digits, optional decimal with up to 2 places
    if (/^-?\d*\.?\d{0,2}$/.test(clean)) {
      const newValues = [...inputValues];
      newValues[index] = clean;
      setInputValues(newValues);

      const parsed = parseFloat(clean);
      const newData = [...data];
      newData[index] = {
        ...newData[index],
        manual_input: isNaN(parsed) ? null : parsed
      };
      updateTableData?.(newData);
    }
  };

  // Fixed keydown handler - allows typing minus first or adding it later
  const handleKeyDown = (e) => {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', '.'];

    if (allowedKeys.includes(e.key)) return;

    const input = e.target;
    const rawValue = input.value;
    const cleanValue = rawValue.replace('₹', '').trim();
    const cursorPos = input.selectionStart;

    // Allow only one decimal point
    if (e.key === '.') {
      if (cleanValue.includes('.')) {
        e.preventDefault();
      }
      return;
    }

    // Allow minus only at the very beginning (position 0 or 1) and only once
    if (e.key === '-') {
      const isAtStart = cursorPos === 0 || cursorPos === 1;
      const hasMinus = cleanValue.startsWith('-');

      if (isAtStart && !hasMinus) {
        return; // allow minus
      } else {
        e.preventDefault();
      }
      return;
    }

    // Block all other keys
    e.preventDefault();
  };

  // Focus handler - places cursor after ₹ for easy editing
  const handleFocus = (e) => {
    e.target.style.borderColor = '#88d8a3';
    e.target.style.boxShadow = '0 0 0 2px rgba(136,216,163,0.1)';

    const val = e.target.value;
    const pos = val.startsWith('₹') ? 1 : 0;
    setTimeout(() => e.target.setSelectionRange(pos, pos), 0);
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = '#e2e8f0';
    e.target.style.boxShadow = 'none';
  };

  const handleSubmit = async () => {
    const payload = {
      business_entity_id: localStorage.getItem("business_entity_id"),
      gstr9c_recon_id: localStorage.getItem("gstr9c_recon_id"),
      records: {
        "16.Tax payable on un-reconciled difference in ITC (due to reasons specified in 13 & 15 above)": data.map((item, idx) => {
          const manualVal = inputValues[idx] === '' ? null : parseFloat(inputValues[idx]);
          return {
            ...item,
            manual_input: manualVal,
            taxable_non_taxable_value: manualVal
          };
        })
      }
    };

    try {
      const res = await axios.post("/gstr9c_manual_data_update", payload);
      setSnackbar({
        open: true,
        message: res.data.message || 'Saved successfully!',
        severity: res.data.status ? 'success' : 'error'
      });
      if (res.data.status) get_gstr9_report_data?.();
    } catch (err) {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    }
  };

  const getRowColor = (i) => ['#e0e7ff', '#e9d5ff', '#f3e8ff', '#ede9fe', '#ddd6fe'][i % 5];

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '30px',
        width: '100%',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={handleBack}
          style={{
            marginBottom: '20px',
            padding: '8px 16px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
            color: '#64748b'
          }}
        >
          Back
        </button>

        <h2 style={{
          marginBottom: '20px',
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '700',
          textAlign: 'center'
        }}>
          {title || 'Table 16 - Tax payable on un-reconciled difference in ITC'}
        </h2>

        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '60px 2fr 1fr',
          gap: '10px',
          fontWeight: '600',
          color: '#475569',
          padding: '15px 10px',
          borderBottom: '2px solid #e2e8f0',
          fontSize: '14px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          marginBottom: '10px',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <span style={{ textAlign: 'center' }}>S.No.</span>
          <span>Description</span>
          <span style={{ textAlign: 'center' }}>Amount</span>
        </div>

        {/* Table Body */}
        <div style={{ maxHeight: 'calc(100vh - 450px)', overflowY: 'auto', marginTop: '10px' }}>
          {data && data.length > 0 ? data.map((item, index) => (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '60px 2fr 1fr',
              gap: '10px',
              alignItems: 'center',
              padding: '10px',
              borderBottom: '1px solid #f1f5f9',
              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
              borderRadius: '6px',
              marginBottom: '2px',
              minHeight: '40px'
            }}>
              <span style={{
                color: '#1e293b',
                fontSize: '13px',
                fontWeight: '600',
                textAlign: 'center',
                padding: '6px',
                backgroundColor: getRowColor(index),
                borderRadius: '6px'
              }}>
                {index + 1}
              </span>

              <span style={{
                color: '#334155',
                fontSize: '13px',
                padding: '6px 10px',
                backgroundColor: '#f1f5f9',
                borderRadius: '6px',
                wordBreak: 'break-word'
              }}>
                {item.description || 'N/A'}
              </span>

              <input
                type="text"
                value={inputValues[index] !== '' ? `₹${inputValues[index]}` : ''}
                onChange={(e) => handleManualInputChange(index, e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder="₹0.00"
                style={{
                  color: '#1e293b',
                  fontWeight: '500',
                  fontSize: '13px',
                  textAlign: 'right',
                  padding: '6px',
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>
          )) : (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
              No data available.
            </div>
          )}
        </div>

        {/* Footer */}
        {data && data.length > 0 && (
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', borderTop: '2px solid #e2e8f0' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#64748b', textAlign: 'center' }}>
              Total Records: {data.length}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleSubmit}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #ffd3a5 0%, #fd9853 50%, #e8a87c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(255,211,165,0.3)'
            }}
          >
            Submit
          </button>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export default Sixteen;